import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import * as contractsApi from '../../api/contracts'
import Button from '../common/Button'
import Spinner from '../common/Spinner'
import PDFViewer from '../common/PDFViewer'
import {
  EnvelopeIcon,
  PaperClipIcon,
  LinkIcon,
  DocumentTextIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ArrowPathIcon,
  EyeIcon
} from '@heroicons/react/24/outline'

/**
 * Preview component for signing link emails and attachments
 * @param {object} props
 * @param {number} props.contractId - Contract ID
 * @param {number} props.trainerId - Trainer ID to preview for
 * @param {boolean} props.isReminder - Whether this is a reminder email
 */
export default function SigningLinkPreview({ contractId, trainerId, isReminder = false, selectedAttachmentIds = [] }) {
  const [activeTab, setActiveTab] = useState('email')
  const [copied, setCopied] = useState(false)

  // Fetch preview data
  const { data: previewData, isLoading, error, refetch } = useQuery({
    queryKey: ['contract-signing-preview', contractId, trainerId, isReminder, selectedAttachmentIds],
    queryFn: () => contractsApi.getSigningLinkPreview(contractId, {
      trainer_id: trainerId,
      is_reminder: isReminder,
      attachment_ids: selectedAttachmentIds.length > 0 ? selectedAttachmentIds : undefined
    }),
    enabled: !!contractId && !!trainerId
  })

  const handleCopyUrl = async () => {
    if (!previewData?.signing_url) return
    
    try {
      await navigator.clipboard.writeText(previewData.signing_url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = previewData.signing_url
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  // Only show contract preview tab if main contract exists
  // Check if we have a contract ID and at least one attachment marked as main contract
  const hasMainContract = previewData?.contract?.id &&
                          previewData?.attachments?.length > 0 &&
                          previewData.attachments.some(att => att.is_main_contract === true)
  
  const tabs = [
    { id: 'email', label: 'Email Preview', icon: EnvelopeIcon },
    ...(hasMainContract ? [{ id: 'contract', label: 'Contract Preview', icon: EyeIcon }] : []),
    { id: 'attachments', label: 'Attachments', icon: PaperClipIcon },
    { id: 'link', label: 'Signing Link', icon: LinkIcon }
  ]

  if (isLoading) {
    return (
      <div className="border rounded-lg p-8 bg-gray-50">
        <div className="flex items-center justify-center">
          <Spinner size="md" />
          <span className="ml-3 text-sm text-gray-600">Loading preview...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="border border-red-200 rounded-lg p-6 bg-red-50">
        <div className="text-center">
          <p className="text-sm text-red-600 mb-3">Failed to load preview</p>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>
            <ArrowPathIcon className="h-4 w-4 mr-1" />
            Retry
          </Button>
        </div>
      </div>
    )
  }

  if (!previewData) {
    return null
  }

  // Filter attachments based on selected IDs (only for optional attachments)
  // Main contract is always included
  const filteredAttachments = previewData.attachments?.filter(att =>
    att.is_main_contract || selectedAttachmentIds.includes(att.id)
  ) || []

  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      {/* Tabs */}
      <div className="border-b border-gray-200 bg-gray-50">
        <nav className="flex">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white text-primary-600 border-b-2 border-primary-600'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-4">
        {activeTab === 'email' && (
          <EmailPreviewTab
            emailHtml={previewData.email_html}
            emailSubject={previewData.email_subject}
            trainer={previewData.trainer}
            attachments={filteredAttachments}
          />
        )}
        
        {activeTab === 'contract' && (
          <ContractPreviewTab
            contractId={previewData.contract.id}
          />
        )}
        
        {activeTab === 'attachments' && (
          <AttachmentsTab attachments={filteredAttachments} />
        )}
        
        {activeTab === 'link' && (
          <SigningLinkTab
            signingUrl={previewData.signing_url}
            expiresAt={previewData.expires_at}
            onCopy={handleCopyUrl}
            copied={copied}
          />
        )}
      </div>
    </div>
  )
}

// Contract Preview Tab
function ContractPreviewTab({ contractId }) {
  if (!contractId) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-red-600">No contract ID available</p>
      </div>
    )
  }

  const pdfUrl = `${import.meta.env.VITE_API_URL}/contracts/${contractId}/download`
  
  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>📄 Contract Preview:</strong> This is how the contract PDF will appear to the trainer on the signing page.
        </p>
        <p className="text-xs text-gray-600 mt-2">
          PDF URL: {pdfUrl}
        </p>
      </div>

      <div className="border rounded-lg overflow-hidden bg-white">
        <PDFViewer
          url={pdfUrl}
          className="h-[600px]"
        />
      </div>

      <p className="text-xs text-gray-500 italic">
        The trainer will be able to view, download, and review this contract before signing
      </p>
    </div>
  )
}

// Email Preview Tab
function EmailPreviewTab({ emailHtml, emailSubject, trainer, attachments }) {
  return (
    <div className="space-y-4">
      {/* Email Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="space-y-2 text-sm">
          <div>
            <span className="font-medium text-gray-700">To:</span>
            <span className="ml-2 text-gray-900">{trainer.name} ({trainer.email})</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Subject:</span>
            <span className="ml-2 text-gray-900">{emailSubject}</span>
          </div>
        </div>
      </div>

      {/* Attachments Info */}
      {attachments && attachments.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm font-medium text-green-900 mb-2">
            📎 Files available to trainer ({attachments.length})
          </p>
          <ul className="text-xs text-green-800 space-y-1">
            {attachments.map((att, idx) => (
              <li key={idx} className="flex items-center gap-2">
                <span className="text-green-600">✓</span>
                <span className="font-medium">{att.file_name}</span>
                {att.is_main_contract && (
                  <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded">MAIN CONTRACT</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Email HTML Preview */}
      <div className="border rounded-lg overflow-hidden bg-white">
        <div className="bg-gray-100 px-4 py-2 border-b">
          <p className="text-xs font-medium text-gray-600">Email Preview</p>
        </div>
        <iframe
          srcDoc={emailHtml}
          className="w-full h-96 border-0"
          title="Email Preview"
        />
      </div>

      <p className="text-xs text-gray-500 italic">
        This is how the email will appear to the trainer
      </p>
    </div>
  )
}

// Attachments Tab
function AttachmentsTab({ attachments }) {
  if (!attachments || attachments.length === 0) {
    return (
      <div className="text-center py-8">
        <PaperClipIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
        <p className="text-sm text-gray-500">No additional files</p>
        <p className="text-xs text-gray-400 mt-1">
          Only the main contract will be available
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 mb-4">
        These files will be available to the trainer on the signing page:
      </p>
      
      {attachments.map((attachment, index) => (
        <div
          key={attachment.id || index}
          className={`flex items-start gap-3 p-3 rounded-lg border ${
            attachment.is_main_contract
              ? 'bg-primary-50 border-primary-200'
              : 'bg-gray-50 border-gray-200'
          }`}
        >
          <DocumentTextIcon className={`h-6 w-6 mt-0.5 ${
            attachment.is_main_contract ? 'text-primary-600' : 'text-gray-600'
          }`} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {attachment.file_name}
              {attachment.is_main_contract && (
                <span className="ml-2 text-xs text-primary-600 font-semibold">
                  MAIN CONTRACT
                </span>
              )}
            </p>
            {attachment.description && (
              <p className="text-xs text-gray-600 mt-1">{attachment.description}</p>
            )}
            {attachment.file_size && (
              <p className="text-xs text-gray-500 mt-1">
                {(attachment.file_size / 1024).toFixed(2)} KB
              </p>
            )}
          </div>
        </div>
      ))}
      
      <p className="text-xs text-gray-500 italic mt-4">
        Total: {attachments.length} file{attachments.length !== 1 ? 's' : ''}
      </p>
    </div>
  )
}

// Signing Link Tab
function SigningLinkTab({ signingUrl, expiresAt, onCopy, copied }) {
  const expiryDate = new Date(expiresAt).toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })

  return (
    <div className="space-y-4">
      {/* URL Display */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <label className="block text-xs font-medium text-gray-700 mb-2">
          Signing URL
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={signingUrl}
            readOnly
            className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded text-sm font-mono text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <Button
            variant={copied ? 'success' : 'primary'}
            size="sm"
            onClick={onCopy}
          >
            {copied ? (
              <>
                <CheckIcon className="h-4 w-4 mr-1" />
                Copied!
              </>
            ) : (
              <>
                <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                Copy
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Expiry Info */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
            <span className="text-amber-600 text-lg">⏰</span>
          </div>
          <div>
            <p className="text-sm font-medium text-amber-900">Link Expires</p>
            <p className="text-sm text-amber-700 mt-1">{expiryDate}</p>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm font-medium text-blue-900 mb-2">Usage</p>
        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
          <li>This link will be included in the email automatically</li>
          <li>You can copy and share it manually if needed</li>
          <li>The link is unique to this trainer and contract</li>
          <li>It will expire after 30 days</li>
        </ul>
      </div>
    </div>
  )
}